import React, { useRef } from "react";
import { Controller } from "react-hook-form";
import NumberFormat from "react-number-format";
import DatePicker from "react-datepicker";
import AsyncSelect from "react-select/lib/Async";
import TimePicker from "react-time-picker";
import Select from "react-select";
import get from "lodash/get";
import { maxFloatNumber } from "./hookForms";
import classNames from "classnames";
import Switch from "react-switch";

/**
 * Función para obtener el mensaje de error de un campo
 * @param {object} errors - Objeto de errores
 * @param {string} name - Nombre del campo
 * @return {string} - Mensaje de error
 */
const getErrorMessage = (errors, name) => {
    const errorObj = get(errors, name);
    let error = null;
    if (errorObj) error = errorObj.message;

    return <small className="text-danger">{error}</small>;
};

/**
 * Componente para renderizar un campo de texto con react-hook-form
 * @param {*} props - Props del componente
 * @returns {JSX.Element}
 */
export const NumberField = ({
    control,
    name,
    errors,
    rules,
    thousandSeparator = true,
    decimalScale = 2,
    onEnterAction,
    ...rest
}) => {
    return (
        <Controller
            name={name}
            control={control}
            rules={{
                ...rules,
                max:
                    rules && rules.max
                        ? rules.max
                        : {
                              value: maxFloatNumber,
                              message: `El número es muy grande`,
                          },
            }}
            render={({ field: { onChange, value, ...field } }) => (
                <div>
                    <NumberFormat
                        {...field}
                        {...rest}
                        // onEnter
                        {...(onEnterAction
                            ? {
                                  onKeyDown: (e) => {
                                      if (e.key === "Enter") {
                                          e.preventDefault();
                                          onEnterAction();
                                      }
                                  },
                              }
                            : {})}
                        value={value}
                        onValueChange={(values) => {
                            const { value } = values;
                            onChange(value);
                        }}
                        thousandSeparator={thousandSeparator}
                        decimalScale={decimalScale}
                        fixedDecimalScale={true}
                        customInput="input" // Usa un input estándar
                        className={`form-control ${rest.className || ""} ${
                            rest.disabled ? "disabled-input" : "bg-white"
                        }`}
                        autoComplete="off"
                    />
                    {/* mostrar error de entrada */}
                    {getErrorMessage(errors, name)}
                </div>
            )}
        />
    );
};

/**
 * Componente para renderizar un campo de texto con react-hook-form
 * @param {*} props - Props del componente
 * @returns {JSX.Element}
 */
export const TextField = ({
    control,
    name,
    errors,
    rules,
    onEnterAction,
    ...rest
}) => {
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field }) => (
                <div>
                    <input
                        type="text"
                        {...field}
                        {...rest}
                        // el value nunca debe quedar en null/undefined: si el
                        // formulario resetea el campo así, el input deja de estar
                        // controlado y el texto anterior se queda visible
                        value={field.value || ""}
                        // onEnter
                        {...(onEnterAction
                            ? {
                                  onKeyDown: (e) => {
                                      if (e.key === "Enter") {
                                          e.preventDefault();
                                          onEnterAction();
                                      }
                                  },
                              }
                            : {})}
                        className={`form-control ${rest.disabled ? "disabled-input" : "bg-white"}`}
                        autoComplete="off"
                    />
                    {/* mostrar error de entrada */}
                    {getErrorMessage(errors, name)}
                </div>
            )}
        />
    );
};

/**
 * Componente para renderizar un campo de fecha con react-hook-form
 * @param {*} props - Props del componente
 * @returns {JSX.Element}
 */
export const DateField = ({ control, name, errors, rules, ...rest }) => {
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field: { onChange, value, ...field } }) => (
                <div>
                    <DatePicker
                        {...field}
                        {...rest}
                        selected={value}
                        onChange={onChange}
                        dateFormat={"DD/MM/YYYY"}
                        className="form-control"
                    />
                    {/* mostrar error de entrada */}
                    {getErrorMessage(errors, name)}
                </div>
            )}
        />
    );
};

/**
 * Componente para renderizar un campo de hora con react-hook-form
 * @param {*} props - Props del componente
 * @returns {JSX.Element}
 */
export const TimeField = ({ control, name, errors, rules, ...rest }) => {
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field: { onChange, value, ...field } }) => (
                <div>
                    <input
                        {...field}
                        {...rest}
                        aria-label="Time"
                        type="time"
                        value={value}
                        onChange={onChange}
                        className="form-control text-center"
                    />
                    {/* <TimePicker
                        {...field}
                        {...rest}
                        maxDetail="minute"
                        locale="en-US"
                        disableClock={true}
                        clearIcon={null}
                        value={value}
                        onChange={onChange}
                        format="h:mm a"
                    /> */}
                    {getErrorMessage(errors, name)}
                </div>
            )}
        />
    );
};

/**
 * Componente para renderizar un campo de selección con react-hook-form
 * @param {*} props - Props del componente
 * @returns {JSX.Element}
 */
export const SelectField = ({
    control,
    name,
    errors,
    rules,
    isMulti,
    isSearchable,
    options,
    onChange: customOnChange,
    ...rest
}) => {
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field: { onChange, value, ...field } }) => (
                <div>
                    <Select
                        {...field}
                        {...rest}
                        className="react-select-container"
                        menuPortalTarget={document.body}
                        backspaceRemovesValue={false}
                        isMulti={isMulti}
                        isSearchable={isSearchable}
                        options={options}
                        value={value}
                        // onChange={onChange}
                        onChange={(value) => {
                            onChange(value);
                            if (customOnChange) customOnChange(value);
                        }}
                    />
                    {getErrorMessage(errors, name)}
                </div>
            )}
        />
    );
};

/**
 * Componente para renderizar un campo de selección asíncrono con react-hook-form
 * @param {*} props - Props del componente
 * @returns {JSX.Element}
 */
export const AsyncSelectField = ({
    control,
    name,
    errors,
    rules,
    loadOptions,
    ...rest
}) => {
    const ref = useRef(null);
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field: { onChange, value, ...field } }) => (
                <div>
                    <AsyncSelect
                        {...field}
                        {...rest}
                        ref={ref}
                        cacheOptions
                        backspaceRemovesValue={false}
                        value={value}
                        onChange={onChange}
                        loadOptions={loadOptions}
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                        // onFocus={() => {
                        //     const value = ref.current.props.value;
                        //     if(value) ref.current.select.onInputChange(value.label);
                        //     else ref.current.select.onInputChange(' ');
                        // }}
                        // blurInputOnSelect={true}
                    />
                    {getErrorMessage(errors, name)}
                </div>
            )}
        />
    );
};

export const RadioField = ({
    name,
    value,
    checked,
    onChange,
    onBlur,
    label,
    disabled,
    error,
    touched,
}) => {
    const invalid = touched && error;

    return (
        <React.Fragment>
            <div className="radio c-radio c-radio-nofont d-flex">
                <label className="font-weight-bold">
                    <input
                        type="radio"
                        name={name}
                        value={value}
                        checked={checked}
                        disabled={disabled}
                        onChange={onChange}
                        onBlur={onBlur}
                        className={classNames({ "is-invalid": invalid })}
                    />
                    {label}&nbsp;
                    <span />
                </label>
            </div>

            {invalid && <div className="invalid-feedback">{error}</div>}
        </React.Fragment>
    );
};

export const SwitchField = (props) => {
    const { name, control, label, disabled, rules = {}, className } = props;

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => {
                const { value, onChange } = field;
                const { error } = fieldState;

                return (
                    <div
                        className={`d-flex align-items-center gap-10 ${className || ""}`}
                        style={{ height: "fit-content" }}
                    >
                        {label && (
                            <label className="mb-0 pl-0 text-nowrap">
                                {label}
                            </label>
                        )}
                        <Switch
                            onColor="#007bff"
                            height={18}
                            width={36}
                            disabled={disabled}
                            onChange={onChange}
                            checked={!!value}
                        />

                        {error && (
                            <div className="invalid-feedback d-block">
                                {error.message}
                            </div>
                        )}
                    </div>
                );
            }}
        />
    );
};
